import { Button } from "@/components/ui/button";
import { useRequestId } from "@/hooks/use-request-id";
import React, { useEffect, useState } from "react";
import RightSidebar from "../right-sidebar";
import useUserInfo from "@/hooks/use-user-info";
import { InputWithLabel } from "../input-label";
import { useFeatureName } from "@/hooks/use-pageTitle";
import { Info, Loader2 } from "lucide-react";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import { shouldDisableIdRequest } from "@/lib/date-utils";
import { lowerCaseComparison } from "@/lib/string-util";
import { QueryKeys, STORAGE, STORAGE_KEY, USER_MODE } from "@/constants";
import { DownloadId } from "@/pages/components/id-card/download-id";
import type { GlobalFlag } from "@/model/global-flag";
import { queryClient } from "@/hooks/use-query-client";
import { getItem } from "@/lib/storage-utils";
import { useIDCardQuery } from "@/hooks/use-id-card";
import IDENTIFIERS from "@/constants/test_id";
import {
  LogCategories,
  LogEventTypeEnum,
  LogSubCategories,
  LogTags,
  PANEL,
} from "@/services/logger/logger-types";

const RequestIdCard = () => {
  const userInfo = useUserInfo();
  const { activeCoverage } = useSelector(
    (state: RootState) => state.planCoverageInfo,
  );
  const globalFlag = queryClient.getQueryData([
    QueryKeys.GLOBAL_FLAGS,
    userInfo.username,
  ]);
  const {
    data: idCardData,
    isFetching,
    isError,
    error,
  } = useIDCardQuery(activeCoverage);
  const [openRightSidbar, setOpenRightSidebar] = useState(false);
  const requestIdQuery = useRequestId();
  const featureName = useFeatureName();

  const authData = getItem(STORAGE_KEY.AUTH_DATA, STORAGE.SESSION);
  const globalFlagData = globalFlag?.data?.[0] as GlobalFlag;
  const { requestedAt, isLoaded } = useSelector(
    (state: RootState) => state.requestId,
  );

  const isIdCardDisabled = !isLoaded
    ? true
    : shouldDisableIdRequest(requestedAt);

  useEffect(() => {
    if (!requestIdQuery.isPending) setOpenRightSidebar(false);
  }, [requestIdQuery.isPending]);

  function handleRequestIdCard() {
    setOpenRightSidebar(!openRightSidbar);
  }

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    requestIdQuery.mutate(userInfo);
  }

  const displayDownloadId =
    !isFetching && !isError && !error && Boolean(idCardData?.base64Encoded);

  const displayRequestId =
    globalFlagData?.showRedCardIdRequest &&
    !lowerCaseComparison(authData?.mode, USER_MODE.GUEST);

  const shouldShowMenu = displayDownloadId || displayRequestId;

  if (!isLoaded) return null;
  if (!shouldShowMenu) return null;

  return (
    <>
      <div className="flex items-center gap-6">
        {displayRequestId && (
          <Button
            variant="default"
            onClick={handleRequestIdCard}
            data-testid={IDENTIFIERS.ID_CARD.REQUEST_NEW_ID_CARD_BUTTON}
          >{`Request New ${featureName}`}</Button>
        )}

        {displayDownloadId && <DownloadId />}
      </div>

      {openRightSidbar && (
        <RightSidebar
          title={`Request a New ${featureName}`}
          isOpen={openRightSidbar}
          onClose={() => setOpenRightSidebar(false)}
          logOptions={{
            panelTitleBase: PANEL.REQUEST_NEW_ID_CARD,
            type: LogEventTypeEnum.PAGE_VIEW,
            tag: LogTags.MODULE_ACCESS,
            category: LogCategories.ID_CARD,
            subCategory: LogSubCategories.REQUEST_NEW_ID_CARD,
          }}
        >
          <form
            className="mx-2 my-5 flex flex-col items-center justify-center gap-4 px-2"
            onSubmit={handleSubmit}
          >
            <InputWithLabel
              id="first_name"
              label="Member First Name *"
              value={userInfo.firstname}
              isDisabled
            />
            <InputWithLabel
              id="last_name"
              label="Member Last Name *"
              value={userInfo.lastname}
              isDisabled
            />
            <InputWithLabel
              id="subscriber_id"
              label="Subscriber ID *"
              value={userInfo.plainEmployeeId}
              isDisabled
            />
            <InputWithLabel
              id="email"
              label="Email *"
              value={userInfo.email}
              isDisabled
            />
            <InputWithLabel
              id="address"
              label="Address *"
              value={userInfo.address}
              isDisabled
            />

            <span role="alert" className="w-full text-sm">
              The above information cannot be changed. To update it, contact
              your plan sponsor.
            </span>

            {isIdCardDisabled && (
              <div className="flex w-full items-center gap-3 rounded-lg bg-gray-200 px-3 py-2">
                <Info className="w-4" />
                <span className="text-sm">
                  Your recent ID card request is already in progress.
                </span>
              </div>
            )}

            <Button
              type="submit"
              className="mt-10 w-32"
              disabled={requestIdQuery.isPending || isIdCardDisabled}
              data-testid={
                IDENTIFIERS.ID_CARD.SUBMIT_REQUEST_NEW_ID_CARD_BUTTON
              }
            >
              {requestIdQuery.isPending ? (
                <Loader2 className="mr-2 size-4 animate-spin" />
              ) : (
                "Submit"
              )}
            </Button>
          </form>
        </RightSidebar>
      )}
    </>
  );
};

export default RequestIdCard;
